/**
 * @fileoverview `TransitPanelRows` (WEB-718) — the "Getting there" row list
 * that mounts as an M3 satellite section inside `EventDecisionPanel`'s
 * `children` slot. Design: `docs/design-system/event-v2-m3-panel-satellite-specs.md`
 * §3 (measured from `Event v2 - 1c Resolved.dc.html` in real Chromium).
 *
 * ## Mount contract (spec §0)
 *
 * `EventDecisionPanel` renders `{children}` **bare**, and its card root carries
 * `[&>*:last-child]:border-b-0`. So this component is itself a direct section
 * of the card: it declares its own padding and opts into the shared
 * `PANEL_SECTION_CLASS` bottom rule. The rule is deliberately **not**
 * suppressed when this block happens to be last — the card handles that
 * structurally, in every present/absent permutation of the three satellites.
 * `EventDecisionPanel.tsx` is not edited by this issue (its "Files affected"
 * list in Linear predates WEB-738 and is stale).
 *
 * ## One derivation, never two (spec §3.2)
 *
 * The rows are rendered from the **shared** getting-there options — the same
 * `GettingThereOption[]` that `GettingThereCard` consumes, produced by
 * `lib/gettingThere/buildGettingThereOptionsFromLocation` and optionally
 * merged with Places rows by `lib/gettingThere/mergeGettingThereOptions`.
 * This component performs **no derivation of its own**: it takes the array as
 * a prop, caps it, and maps each option's fields straight through. A second
 * derivation would let the panel and the card contradict each other on one
 * screen, which is the defect this design explicitly guards against.
 *
 * The caller (the M5 panel composer) owns the build/merge, so the Places merge
 * — which is lazy and optional — never blocks this block: the panel renders
 * correctly from `Location` columns alone, and re-renders if/when Places rows
 * arrive.
 *
 * ## The shape mismatch, resolved honestly (spec §3.2)
 *
 * The mockup draws a row as *name + mono sub-line (`ROUTE · 0.6 MI`) +
 * right-aligned time (`9 MIN`)*. The shared builder decomposes the same facts
 * differently, because that is the shape of the data LOCI actually has:
 *
 * | layer     | `name`                     | `detail`            | `metric`   |
 * | --------- | -------------------------- | ------------------- | ---------- |
 * | `walk`    | `Walk to nearest transit`  | `0.2 mi walk`       | `~4 min`   |
 * | `transit` | the real stop name         | `Nearest stop`      | *(absent)* |
 * | `parking` | `On-site parking`          | `At venue`          | *(absent)* |
 *
 * `name` → row name, `detail` → `<small>` sub-line, `metric` → the right-hand
 * time cell. The walk and transit facts are therefore **two rows**, not one.
 * Recombining them into a single mockup-shaped row would attach a walk time to
 * a stop whose distance was never measured — a re-derivation and a fabrication
 * at once. Two rows render in the same visual shape and stay true.
 *
 * ## Never estimate a time (spec §3.3)
 *
 * `metric` is the only source of the right-hand cell, and the builder emits it
 * **only** from a real metre value, converted at a fixed
 * `WALK_METERS_PER_MINUTE = 80` and always prefixed `~`. An option without
 * `metric` renders its sub-line and **no time cell at all** — never a dash,
 * never a guess. Nothing is estimated at this render layer.
 *
 * ## Order and cap (spec §3.2 / §3.4)
 *
 * Rendered in the order the builder returns, which is already canonicalised to
 * `walk → transit → parking → bike` — this component does **not** re-sort.
 * Capped at the design's two rows (its degraded capture drops the parking row
 * and keeps the heading, so one row is a normal state and zero rows renders
 * nothing at all).
 *
 * ## Tokens
 *
 * Design `--teal` (spec §4: `#4FC4C4` dark / `#0E7B7B` light) maps onto the
 * repo's `--clt-skyline-glow-fg`, which is the token defined as a teal
 * *foreground*: it is `#4fc4c4` in dark — an exact match — and `#0a6363` in
 * light, marginally darker than the design's value and therefore strictly
 * higher contrast. `GettingThereCard` already uses this same token for its
 * teal text, so the two surfaces cannot drift. Design `--ink`/`--ink3` map to
 * `--fg-1`/`--fg-3` and `--line` to `--border` (via `PANEL_SECTION_CLASS`).
 *
 * `'use client'` is required for `useId` (project lint rule FRE-887), matching
 * `RunOfDatesStrip` — the shipped precedent for a panel satellite molecule.
 */
'use client';

import { useId, type ReactElement } from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { PANEL_SECTION_CLASS } from '@/components/organisms/EventDecisionPanel';
import type { GettingThereOption } from '@/components/organisms/GettingThereCard';
import { EVT_MICRO_LABEL_10_MONO_CLASS } from '@/lib/eventV2MicroLabel';

/**
 * The design's row cap (spec §3.4). The mockup shows two rows at full data and
 * drops to one under field omission, so this is a maximum, not a target.
 */
export const TRANSIT_PANEL_ROW_LIMIT = 2;

/**
 * The heading is a literal constant, not a lexicon lookup: it measured
 * byte-identical across all ten category slugs in the design source
 * (spec §3.1).
 */
export const TRANSIT_PANEL_HEADING = 'Getting there';

/**
 * Selects the options this block renders: the **first**
 * `TRANSIT_PANEL_ROW_LIMIT`, in the order the shared builder returned them.
 * Pure, and deliberately the whole of the selection logic — exported so the
 * mini-map satellite (WEB-716), whose satellite dots must correspond
 * one-to-one with these rows, can reuse the identical cap rather than
 * re-deriving it.
 */
export function resolveTransitPanelRows(
  options: readonly GettingThereOption[] | null | undefined,
): readonly GettingThereOption[] {
  if (!options || options.length === 0) {
    return [];
  }
  return options.slice(0, TRANSIT_PANEL_ROW_LIMIT);
}

export interface TransitPanelRowsProps {
  /**
   * The shared getting-there options — already built (and, where available,
   * Places-merged) by the caller via `lib/gettingThere/*`. Never re-derived
   * here. An empty/absent array renders nothing.
   */
  options: readonly GettingThereOption[] | null | undefined;
  className?: string;
}

/**
 * `TransitPanelRows` — the panel's "Getting there" section. Returns `null`
 * when there is no honest row to show, which also removes the section rule
 * (spec §6: "renders nothing when the shared builder returns `[]`").
 */
export function TransitPanelRows({
  options,
  className,
}: TransitPanelRowsProps): ReactElement | null {
  // Hooks run unconditionally, before the early return (same posture as
  // `RunOfDatesStrip`).
  const headingId = useId();
  const rows = resolveTransitPanelRows(options);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(PANEL_SECTION_CLASS, 'px-5 py-[18px]', className)}
      data-testid="transit-panel-rows"
    >
      <p
        id={headingId}
        data-testid="transit-panel-rows-heading"
        className={cn(EVT_MICRO_LABEL_10_MONO_CLASS, 'text-clt-skyline-glow-fg')}
      >
        {TRANSIT_PANEL_HEADING}
      </p>

      <ul
        aria-labelledby={headingId}
        data-testid="transit-panel-rows-list"
        className="mt-3 flex list-none flex-col gap-[10px] p-0 text-small text-fg-1"
      >
        {rows.map((option) => (
          <li
            key={`${option.layer}-${option.name}`}
            data-testid="transit-panel-row"
            data-layer={option.layer}
            className="grid grid-cols-[1fr_auto] items-baseline gap-[10px]"
          >
            <span data-testid="transit-panel-row-name">
              {option.name}
              {/*
                The design's sub-line carries NO `text-transform` (unlike the
                heading above it) — the mockup's sub-line strings are literally
                uppercase. Render whatever casing the builder produced; never
                `.toUpperCase()` it here.
              */}
              <small
                data-testid="transit-panel-row-detail"
                className="mt-[2px] block text-micro font-normal tracking-[0.06em] text-fg-3 font-mono"
              >
                {option.detail}
              </small>
            </span>

            {/*
              No `metric` ⇒ no time cell at all. The `auto` grid track
              collapses, the sub-line still renders, and nothing is estimated
              to fill the gap (spec §3.3).
            */}
            {option.metric != null && option.metric.length > 0 ? (
              <span
                data-testid="transit-panel-row-metric"
                className="text-caption text-clt-skyline-glow-fg font-mono"
              >
                {option.metric}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
